import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupportAlerts } from './SupportAlertsProvider';
import { formatTime, mergeMessages, supportApi } from './supportApi';
import SupportMessage from './SupportMessage';
import SupportCallTimeline from './SupportCallTimeline';
import VoiceRecorder from './VoiceRecorder';
import SupportEmojiPicker from './SupportEmojiPicker';
import './SupportInbox.css';

export default function SupportInbox() {
  const { apiUrl, token, socket, connected, lastAlert, error: socketError, refreshUnread, enableAlerts, soundOn, callCustomer } = useSupportAlerts();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(() => new URLSearchParams(window.location.search).get('chat') || '');
  const selectedRef = useRef(selected);
  const [messages, setMessages] = useState([]);
  const [threadCalls, setThreadCalls] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [draft, setDraft] = useState('');
  const [showChatMobile, setShowChatMobile] = useState(Boolean(selected));
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [callHistory, setCallHistory] = useState(null);
  const [callHistoryScope, setCallHistoryScope] = useState('all');
  const [callStatus, setCallStatus] = useState(null);
  const [canManageVideo, setCanManageVideo] = useState(false);
  const [typing, setTyping] = useState(false);
  const fileInput = useRef(null);
  const textAreaRef = useRef(null);
  const endOfThread = useRef(null);
  const cursorRef = useRef(null);
  const readTimer = useRef(null);
  const notifyTypingTimer = useRef(null);
  const lastTypingSent = useRef(0);
  const busyRef = useRef(false);

  selectedRef.current = selected;

  const request = useCallback((path, method = 'GET', body) => supportApi({ apiUrl, token, path, method, body }), [apiUrl, token]);
  const loadCallHistory = useCallback(async (scope = 'all') => {
    try {
      const query = scope === 'all' ? '' : `?conversationId=${encodeURIComponent(scope)}`;
      const result = await request(`/calls/history${query}`);
      setCallHistoryScope(scope);
      setCallHistory(result.calls || []);
      setAgentError('');
    } catch (err) { setAgentError(err.message); }
  }, [request]);

  useEffect(() => {
    if (!selected || !token) { setThreadCalls([]); return; }
    let active = true;
    const refresh = async () => {
      try {
        const result = await request(`/calls/history?conversationId=${encodeURIComponent(selected)}`);
        if (active) setThreadCalls(result.calls || []);
      } catch (e) { if (active) console.warn('[support call timeline]', e); }
    };
    setThreadCalls([]);
    void refresh();
    const timer = setInterval(refresh, 12000);
    return () => { active = false; clearInterval(timer); };
  }, [selected, token, request]);
  const timeline = useMemo(() => [
    ...messages.map(message => ({type:'message', key:`message:${message._id}`,
      time:new Date(message.createdAt).getTime(), message})),
    ...threadCalls.map(call => ({type:'call', key:`call:${call._id}`,
      time:new Date(call.startedAt || call.createdAt || '').getTime(), call})),
  ].sort((a,b) => a.time - b.time), [messages, threadCalls]);

  const insertEmoji = (emoji) => {
    const input = textAreaRef.current;
    const start = input?.selectionStart ?? draft.length;
    const end = input?.selectionEnd ?? start;
    const updated = `${draft.slice(0, start)}${emoji}${draft.slice(end)}`.slice(0, 3000);
    setDraft(updated);
    requestAnimationFrame(() => {
      const current = textAreaRef.current;
      const caret = Math.min(start + emoji.length, updated.length);
      current?.focus(); current?.setSelectionRange(caret, caret);
    });
  };

  const loadInbox = useCallback(async () => {
    if (!token) return;
    try {
      const data = await request('/agents/conversations');
      setConversations(data.conversations || []);
      setAgentError('');
      void refreshUnread();
    } catch (err) { setAgentError(err.message); }
  }, [request, token, refreshUnread]);

  const markRead = useCallback((conversationId) => {
    if (!socket?.connected || !conversationId || document.hidden) return;
    socket.emit('support:read', { conversationId }, () => { void refreshUnread(); });
    setConversations(chats => chats.map(c => c._id === conversationId ? { ...c, unreadForAgent: 0 } : c));
  }, [socket, refreshUnread]);

  const loadThread = useCallback(async (id, older = false) => {
    if (!id || !token) return;
    if (!older) setLoading(true);
    try {
      const cursor = older ? cursorRef.current : '';
      const suffix = cursor ? `?before=${encodeURIComponent(cursor)}` : '';
      const data = await request(`/agents/conversations/${encodeURIComponent(id)}/messages${suffix}`);
      if (selectedRef.current !== id) return;
      setMessages(prev => older ? mergeMessages(data.messages || [], prev) : data.messages || []);
      setHasMore(Boolean(data.hasMore));
      if (!older) markRead(id);
    } catch (err) { setAgentError(err.message); }
    finally { if (!older) setLoading(false); }
  }, [request, token, markRead]);

  const openThread = useCallback((id) => {
    if (!id) return;
    selectedRef.current = id;
    setSelected(id);
    setShowChatMobile(true);
    setMessages([]);
    cursorRef.current = null;
    setHasMore(false);
    setAgentError('');
    setTyping(false);
    window.history.replaceState(null, '', `${window.location.pathname}?chat=${encodeURIComponent(id)}`);
    if (socket?.connected) {
      socket.timeout(10000).emit('support:join', { conversationId: id }, (err, result) => {
        if (err || !result?.ok) setAgentError(result?.error || 'Could not join chat');
        else markRead(id);
      });
    }
    void loadThread(id);
  }, [socket, loadThread, markRead]);

  useEffect(() => { void loadInbox(); }, [loadInbox]);
  useEffect(() => {
    if (!token) return;
    request('/calls/capabilities').then(r => setCanManageVideo(r.canManageVideo === true))
      .catch(() => setCanManageVideo(false));
  }, [token, request]);
  useEffect(() => {
    if (!socket) return;
    const onConnect = () => {
      void loadInbox();
      if (selectedRef.current) {
        socket.emit('support:join', { conversationId: selectedRef.current }, (response) => {
          if (response?.ok) markRead(selectedRef.current);
        });
        void loadThread(selectedRef.current);
      }
    };
    const changed = () => { void loadInbox(); };
    const onMessage = (message) => {
      if (message.conversationId === selectedRef.current) {
        setMessages(prev => mergeMessages(prev, [message]));
        if (message.senderRole === 'user') markRead(message.conversationId);
      }
      void loadInbox();
    };
    const onTyping = (data) => {
      if (data.conversationId !== selectedRef.current || data.role !== 'user') return;
      setTyping(Boolean(data.typing));
      if (readTimer.current) clearTimeout(readTimer.current);
      if (data.typing) readTimer.current = setTimeout(() => setTyping(false), 3000);
    };
    socket.on('connect', onConnect);
    socket.on('support:conversation_changed', changed);
    socket.on('support:message', onMessage);
    socket.on('support:typing', onTyping);
    if (socket.connected) onConnect();
    return () => {
      socket.off('connect', onConnect);
      socket.off('support:conversation_changed', changed);
      socket.off('support:message', onMessage);
      socket.off('support:typing', onTyping);
      if (readTimer.current) clearTimeout(readTimer.current);
    };
  }, [socket, loadInbox, loadThread, markRead]);

  useEffect(() => { if (lastAlert) void loadInbox(); }, [lastAlert, loadInbox]);
  useEffect(() => { cursorRef.current = messages[0]?._id || null; }, [messages]);
  useEffect(() => { endOfThread.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages.length, selected]);
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) { if (selectedRef.current) markRead(selectedRef.current); void loadInbox(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [markRead, loadInbox]);
  useEffect(() => () => { if (readTimer.current) clearTimeout(readTimer.current); if (notifyTypingTimer.current) clearTimeout(notifyTypingTimer.current); }, []);

  // A disabled-looking button is not useful: keep the buttons clickable and
  // surface the actual presence/auth/TURN error instead.
  useEffect(() => {
    if (!selected || !token) { setCallStatus(null); return; }
    let alive = true;
    const refresh = async () => {
      try {
        const result = await request(`/agents/conversations/${encodeURIComponent(selected)}/call-status`);
        if (alive) setCallStatus(result);
      } catch (e) { if (alive) setCallStatus({ online: false, error: e.message }); }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 12000);
    // Route transitions broadcast instantly from customer's EXISTING socket.
    const onAvailability = ({ customerId }) => {
      const currentChat = conversations.find(c => c._id === selected);
      const targetId = typeof currentChat?.userId === 'object' ? String(currentChat.userId?._id || '') : String(currentChat?.userId || '');
      if (String(customerId) === targetId) void refresh();
    };
    socket?.on('support:availability', onAvailability);
    return () => { alive = false; clearInterval(timer); socket?.off('support:availability', onAvailability); };
  }, [selected, token, request, socket, conversations]);

  const current = conversations.find(c => c._id === selected);
  const visible = useMemo(() => conversations.filter(c => {
    if (filter === 'unread' && !c.unreadForAgent) return false;
    if (filter === 'open' && c.status !== 'open') return false;
    if (filter === 'closed' && c.status !== 'closed') return false;
    const user = typeof c.userId === 'object' && c.userId ? c.userId : {};
    return `${user.Name || ''} ${user.userUniqueId || ''} ${c.lastMessageText || ''}`.toLowerCase().includes(search.trim().toLowerCase());
  }), [conversations, search, filter]);
  const userOf = c => typeof c?.userId === 'object' && c.userId ? c.userId : {};

  const sendText = async e => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || !selected || !socket?.connected || busyRef.current) return;
    busyRef.current = true; setSending(true); setAgentError('');
    const clientId = crypto.randomUUID();
    try {
      const result = await new Promise((resolve, reject) => socket.timeout(15000).emit('support:send',
        { conversationId: selected, text: content.slice(0, 3000), clientId },
        (err, reply) => err ? reject(new Error('Message timed out. Check history before retrying.')) : resolve(reply)
      ));
      if (!result?.ok) throw new Error(result?.error || 'Could not send message');
      if (result.message) setMessages(prev => mergeMessages(prev, [result.message]));
      setDraft(''); setTyping(false);
      socket.emit('support:typing', { conversationId: selected, typing: false });
      void loadInbox();
    } catch (err) { setAgentError(err.message); }
    finally { busyRef.current = false; setSending(false); }
  };
  const sendFile = async (file, kind = 'file', durationMs = 0) => {
    if (!selected || !token) throw new Error('Select a customer first');
    if (busyRef.current) throw new Error('Previous message is still sending');
    const maxUploadMb = kind === 'voice' ? 12 : file.type.startsWith('image/') ? 20 : 12;
    if (file.size > maxUploadMb * 1024 * 1024) throw new Error(`File too large. Maximum ${maxUploadMb} MB.`);
    const chatId = selected;
    const form = new FormData();
    form.append('file', file); form.append('kind', kind);
    form.append('conversationId', chatId); form.append('durationMs', String(durationMs));
    busyRef.current = true; setSending(true); setAgentError('');
    try {
      const result = await request('/upload', 'POST', form);
      if (selectedRef.current === chatId) setMessages(prev => mergeMessages(prev, [result.message]));
      void loadInbox();
    } catch (err) { setAgentError(err.message); throw err; }
    finally { busyRef.current = false; setSending(false); }
  };
  const closeTicket = async () => {
    if (!selected) return;
    try { await request(`/agents/conversations/${selected}/close`, 'PATCH'); void loadInbox(); }
    catch (err) { setAgentError(err.message); }
  };
  const notifyTyping = () => {
    if (!socket?.connected || !selected) return;
    const now = Date.now();
    if (now - lastTypingSent.current > 1400) {
      socket.emit('support:typing', { conversationId: selected, typing: true });
      lastTypingSent.current = now;
    }
    if (notifyTypingTimer.current) clearTimeout(notifyTypingTimer.current);
    notifyTypingTimer.current = setTimeout(() => socket.emit('support:typing', { conversationId: selected, typing: false }), 1800);
  };

  return <main className="gp-support-page">
    <div className="gp-support-topbar">
      <div className="gp-support-heading"><h1>Customer Support Inbox</h1><p>Live customer conversations · Voice · Attachments</p></div>
      <div className="gp-top-actions">
        <span className={`gp-status ${connected ? 'gp-online' : ''}`}>{connected ? '● Live connected' : '○ Connecting…'}</span>
        <button type="button" className="gp-outline-btn" onClick={() => void enableAlerts()}>{soundOn ? '🔊 Sound on' : '🔊 Enable ringing sound'}</button>
        <button type="button" className="gp-outline-btn" onClick={() => {
          void loadCallHistory('all');
        }}>☎ Call history</button>
        <button type="button" className="gp-outline-btn" onClick={() => void loadInbox()}>↻ Refresh</button>
      </div>
    </div>
    {(agentError || socketError) && <div className="gp-banner-error" role="alert">{agentError || socketError}</div>}
    {!token && <div className="gp-banner-error">No admin token. Provide the admin JWT to SupportAlertsProvider.</div>}
    <div className="gp-inbox-layout">
      <aside className={`gp-threads ${showChatMobile ? 'gp-mobile-hide' : ''}`}>
        <header className="gp-threads-header"><strong>Conversations <span>{conversations.length}</span></strong>
          <input type="search" placeholder="Search user or message…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="gp-filters">{['all','unread','open','closed'].map(f => <button key={f} type="button" className={filter === f ? 'gp-filter-on' : ''} onClick={() => setFilter(f)}>{f[0].toUpperCase()+f.slice(1)}</button>)}</div>
        </header>
        <div className="gp-thread-items">{visible.map(c => {
          const user = userOf(c);
          return <button type="button" key={c._id} className={`gp-thread ${selected === c._id ? 'gp-thread-active' : ''}`} onClick={() => openThread(c._id)}>
            <span className="gp-avatar">{String(user.Name || 'U').charAt(0).toUpperCase()}</span>
            <span className="gp-thread-info"><span className="gp-thread-title"><b>{user.Name || 'Customer'}</b><small>{formatTime(c.lastMessageAt)}</small></span>
              <span className="gp-thread-id">{user.userUniqueId || c._id.slice(-8)}</span>
              <span className="gp-thread-preview">{c.lastMessageText || 'New conversation'}</span></span>
            {c.unreadForAgent > 0 && <span className="gp-thread-unread">{Math.min(99, c.unreadForAgent)}</span>}
          </button>;
        })}{!visible.length && <p className="gp-emptystate">No matching conversations</p>}</div>
      </aside>
      <section className={`gp-thread-panel ${!showChatMobile ? 'gp-mobile-hide' : ''}`}>
        {!selected ? <div className="gp-empty-chat"><span>💬</span><h2>Select a customer conversation</h2><p>Reply instantly, send a voice note, or share a file.</p></div> : <>
          <header className="gp-chat-header">
            <div className="gp-chat-identity">
              <button type="button" className="gp-back" aria-label="Back to conversations" onClick={() => setShowChatMobile(false)}>←</button>
              <span className="gp-avatar">{String(userOf(current).Name || 'U').charAt(0).toUpperCase()}</span>
              <div className="gp-chat-heading">
                <b>{userOf(current).Name || 'Customer'}</b>
                <small>{userOf(current).userUniqueId || selected} · {current?.status || 'open'} · {callStatus?.inGame ? '🎮 In Ludo — calls paused' : callStatus?.online ? '🟢 Available for calls' : '⚪ Offline'}</small>
              </div>
            </div>
            <div className="gp-chat-actions" role="group" aria-label="Conversation actions">
              <button type="button" className="gp-outline-btn gp-call-action" title="Call history for this customer" onClick={() => void loadCallHistory(selected)}>🕘 <span>Call log</span></button>
              <button type="button" className="gp-outline-btn gp-call-action" disabled={callStatus?.inGame === true}
                title={callStatus?.inGame ? 'Customer is on the Ludo game screen' : 'Start a support voice call'}
                onClick={() => { void callCustomer(selected, 'audio').then(() => setAgentError('')).catch(e => setAgentError(e.message)); }}>📞 <span>Voice call</span></button>
              <button type="button" className="gp-outline-btn gp-call-action"
                disabled={callStatus?.inGame === true || !(userOf(current).supportVideoAllowed === true || callStatus?.videoAllowed === true)}
                title={callStatus?.inGame ? 'Customer is playing Ludo' :
                  !(userOf(current).supportVideoAllowed === true || callStatus?.videoAllowed === true) ? 'Admin must allow video for this customer first' : 'Start video call'}
                onClick={() => { void callCustomer(selected, 'video').then(() => setAgentError('')).catch(e => setAgentError(e.message)); }}>📹 <span>Video call</span></button>
              {canManageVideo && <button type="button" className="gp-outline-btn gp-manage-action" title="Only Admin can change video permission"
                onClick={async () => {
                  const customerId = userOf(current)._id;
                  if (!customerId) { setAgentError('Customer ID missing.'); return; }
                  try {
                    const allowed = !(userOf(current).supportVideoAllowed === true || callStatus?.videoAllowed === true);
                    const result = await request(`/agents/video-access/${customerId}`, 'PATCH', { allowed });
                    setConversations(old => old.map(c => c._id === selected && typeof c.userId === 'object'
                      ? { ...c, userId: { ...c.userId, supportVideoAllowed: result.allowed } } : c));
                    setCallStatus(previous => previous ? { ...previous, videoAllowed: result.allowed } : previous);
                    setAgentError('');
                  } catch (e) { setAgentError(e.message); }
                }}>{(userOf(current).supportVideoAllowed === true || callStatus?.videoAllowed === true) ? '🎥 Revoke video' : '🎥 Allow video'}</button>}
              <button type="button" className="gp-outline-btn gp-close-btn" disabled={current?.status === 'closed'}
                onClick={() => void closeTicket()}>✓ <span>Close ticket</span></button>
            </div>
          </header>
          <div className="gp-chat-scroll">
            {hasMore && <button type="button" className="gp-load-more" disabled={loading} onClick={() => void loadThread(selected, true)}>↑ Load older messages</button>}
            {loading && <div className="gp-emptystate">Loading messages…</div>}
            {!loading && !messages.length && <div className="gp-emptystate">No messages in this conversation yet</div>}
            {timeline.map(item => item.type === 'call'
              ? <SupportCallTimeline key={item.key} call={item.call} />
              : <SupportMessage key={item.key} message={item.message} apiUrl={apiUrl} token={token} />)}
            {typing && <p className="gp-user-typing">Customer is typing…</p>}
            <div ref={endOfThread} />
          </div>
          <footer className="gp-chat-footer">
            <div className="gp-compose-toolbar"><input type="file" ref={fileInput} className="gp-visually-hidden" accept="image/*,.pdf,.txt,.csv,.docx" onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void sendFile(file).catch(() => {}); }} />
              <button type="button" className="gp-icon-btn" title="Attach photo, PDF or document" disabled={sending} onClick={() => fileInput.current?.click()}>📎</button>
              <VoiceRecorder key={selected} disabled={sending} onSend={(file, durationMs) => sendFile(file, 'voice', durationMs)} />
              <SupportEmojiPicker disabled={sending} onSelect={insertEmoji} />
              <span className="gp-composer-tip">Record → Preview → Send manually</span>
            </div>
            <form className="gp-compose-form" onSubmit={sendText}>
              <textarea ref={textAreaRef} rows={2} maxLength={3000} value={draft} placeholder="Type your reply…" disabled={sending} onChange={e => { setDraft(e.target.value); notifyTyping(); }} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendText(e); } }} />
              <button type="submit" className="gp-send" disabled={sending || !connected || !draft.trim()}>{sending ? 'Sending…' : '➤ Send'}</button>
            </form>
          </footer>
        </>}
      </section>
    </div>
    {callHistory !== null && <div className="gp-call-history-backdrop" role="dialog" aria-modal="true" aria-label="Support call history" onClick={e => { if (e.target === e.currentTarget) setCallHistory(null); }}>
      <div className="gp-call-history-card">
        <div className="gp-call-history-heading">
          <div><h2>{callHistoryScope === 'all' ? 'Support call history' : 'Customer call history'}</h2>
            <p>Answered, missed, declined · last 60 calls</p></div>
          <div className="gp-call-history-heading-actions">
            <button type="button" onClick={() => void loadCallHistory(callHistoryScope)}>↻ Refresh</button>
            <button type="button" onClick={() => setCallHistory(null)}>✕ Close</button>
          </div>
        </div>
        {!callHistory.length && <p className="gp-call-history-empty">No calls recorded for this view.</p>}
        <div className="gp-call-history-list">{callHistory.map(c => {
          const talked = Math.max(0, Number(c.talkDurationSeconds) || 0);
          const rang = Math.max(0, Number(c.ringDurationSeconds) || 0);
          const formatDuration = seconds => seconds >= 3600
            ? `${Math.floor(seconds / 3600)}h ${Math.floor(seconds % 3600 / 60)}m ${seconds % 60}s`
            : seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
          return <div key={c._id} className="gp-call-history-row">
            <span className="gp-call-history-icon">{c.kind === 'video' ? '📹' : '📞'}</span>
            <span className="gp-call-history-info">
              <strong>{c.kind === 'video' ? 'Video' : 'Voice'} call · {c.result}</strong>
              <small>{c.answered ? `Call duration ${formatDuration(talked)}` : `Rang ${formatDuration(rang)}`}</small>
              <small>{new Date(c.startedAt || c.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</small>
              {callHistoryScope === 'all' && <small>Customer: {c.customerName || 'Customer'}</small>}
            </span>
            <span className={`gp-call-history-outcome ${c.answered ? 'gp-answered' : 'gp-unanswered'}`}>{c.answered ? '✓' : '↘'}</span>
          </div>;
        })}</div>
      </div>
    </div>}
  </main>;
}
